// /expat-relocation/$topic/$city — TH long-tail combo page. Phase P-EXPAT-CITY-COMBOS.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import type { ExpatTopic, ExpatCategory } from "@/data/expat-relocation";
import { EXPAT_TOPICS_LITE, EXPAT_LITE_BY_CATEGORY, EXPAT_CAT_LABELS_TH, EXPAT_CAT_LABELS_EN } from "@/data/expat-index.gen";
import { EXPAT_CITIES, type ExpatCity } from "@/data/expat-cities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, AlertTriangle, MapPin, Building2, Wallet, Wifi, Cloud, Users, ChevronRight, CheckCircle2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/expat-relocation/$topic/$city")({
  loader: async ({ params }) => {
    const { EXPAT_TOPICS } = await import("@/data/expat-relocation");
    const topic = EXPAT_TOPICS.find((t) => t.slug === params.topic);
    const { findExpatCity } = await import("@/data/expat-cities");
    const city = findExpatCity(params.city);
    if (!topic || !city) throw notFound();
    return { topic, city };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { topic: t, city: c } = loaderData;
    const url = `${SITE}/expat-relocation/${t.slug}/${c.slug}`;
    const snippet = c.categories[t.category];
    const title = `${t.titleTh} ใน${c.nameTh} — ค่าใช้จ่าย คำแนะนำท้องถิ่น 2026 | NYC`;
    const desc = `${snippet.introTh.slice(0, 140)}... Get your checklist in minutes.`.slice(0, 158);
    const localFaqs = buildLocalFaqs(t, c);
    return {
      meta: [
        { title: ctaTitle(title, "/expat-relocation/$topic/$city", "th") }, { name: "description", content: ctaDesc(desc, "/expat-relocation/$topic/$city", "th")},
        { property: "og:title", content: ctaTitle(title, "/expat-relocation/$topic/$city", "th")}, { property: "og:description", content: ctaDesc(desc, "/expat-relocation/$topic/$city", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/expat-relocation/${t.slug}/${c.slug}`, `/en/expat-relocation/${t.slug}/${c.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${t.titleTh} ใน${c.nameTh}`, serviceType: `${t.titleEn} in ${c.nameEn}`, description: desc,
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: { "@type": "City", name: c.nameEn, geo: { "@type": "GeoCoordinates", latitude: c.lat, longitude: c.lng } },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Place", "@id": `${url}#place`,
              name: c.nameTh, address: { "@type": "PostalAddress", addressLocality: c.nameEn, addressRegion: c.region, addressCountry: "TH" },
              geo: { "@type": "GeoCoordinates", latitude: c.lat, longitude: c.lng },
            }),
          },
          faqPageSchema(localFaqs),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "Expat Relocation", item: `${SITE}/expat-relocation` },
                { "@type": "ListItem", position: 3, name: t.titleTh, item: `${SITE}/expat-relocation/${t.slug}` },
                { "@type": "ListItem", position: 4, name: c.nameTh, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">ไม่พบหน้านี้</h1>
      <Link to="/expat-relocation" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-bold text-primary-foreground">กลับไปหน้ารวม</Link>
    </main>
  ),
  errorComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-bold">เกิดข้อผิดพลาด</h1>
      <Link to="/expat-relocation" className="mt-4 inline-block text-primary">กลับไปหน้ารวม</Link>
    </main>
  ),
  component: ExpatComboTh,
});

function buildLocalFaqs(t: ExpatTopic, c: ExpatCity) {
  const s = c.categories[t.category];
  return [
    { q: `${t.titleTh} ใน${c.nameTh} ราคาเท่าไหร่?`, a: `${s.costNoteTh} เทียบกับค่าทั่วประเทศ ${t.costTh}` },
    { q: `แถวไหนใน${c.nameTh}เหมาะสำหรับเอ็กซ์แพท?`, a: `เขตยอดนิยม: ${c.districtsTh.slice(0, 4).join(", ")}` },
    { q: `ใช้เวลาเท่าไหร่ในการ${t.titleTh}?`, a: `ระยะเวลามาตรฐาน ${t.timelineTh} ใน${c.nameTh}คล้ายกัน — สามารถนัดล่วงหน้าผ่าน NYC เพื่อให้เสร็จในวันเดียว` },
    { q: `อินเทอร์เน็ตและการเดินทางใน${c.nameTh}เป็นยังไง?`, a: `${c.internetTh}. การเดินทาง: ${c.transitTh}` },
    { q: `อากาศใน${c.nameTh}เหมาะตอนไหน?`, a: c.climateTh },
    ...t.faqsTh.slice(0, 3),
  ];
}

function ExpatComboTh() {
  const { topic: t, city: c } = Route.useLoaderData() as { topic: ExpatTopic; city: ExpatCity };
  const s = c.categories[t.category];
  const otherCities = EXPAT_CITIES.filter((x) => x.slug !== c.slug).slice(0, 6);
  const otherTopicsSameCat = EXPAT_TOPICS_LITE.filter((x) => x.category === t.category && x.slug !== t.slug).slice(0, 5);

  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <Link to="/expat-relocation" className="hover:text-primary">Expat Relocation</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <Link to="/expat-relocation/$topic" params={{ topic: t.slug }} className="hover:text-primary">{t.titleTh}</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <span>{c.nameTh}</span>
          </nav>
          <div className="text-xs font-bold uppercase tracking-widest text-primary">{EXPAT_CAT_LABELS_TH[t.category]} · {c.nameTh}</div>
          <h1 className="mt-2 text-3xl font-extrabold sm:text-5xl">{t.titleTh} ใน{c.nameTh}</h1>
          <p className="mt-3 max-w-3xl text-lg text-muted-foreground">{t.taglineTh}</p>

          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <p className="text-sm leading-relaxed text-foreground sm:text-base">{s.introTh}</p>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-4 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3">
              <Wallet className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">ค่าใช้จ่ายท้องถิ่น</div>
              <div className="text-xs font-bold leading-tight">{s.costNoteTh}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <MapPin className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">ภูมิภาค</div>
              <div className="text-sm font-bold">{c.region}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <Users className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">Cost Index (BKK=100)</div>
              <div className="text-sm font-bold">{c.costIndex}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <Cloud className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">อากาศ</div>
              <div className="text-[11px] font-semibold leading-tight">{c.climateTh.slice(0, 60)}</div>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl font-extrabold"><Building2 className="mr-2 inline h-5 w-5 text-primary" />สถานที่/ตัวเลือกแนะนำใน{c.nameTh}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{c.profileTh}</p>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {s.placesTh.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">เคล็ดลับเฉพาะ{c.nameTh}</h2>
              <ol className="mt-4 space-y-3">
                {s.tipsTh.map((tip, i) => (
                  <li key={i} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{i + 1}</div>
                      <p className="text-sm text-foreground">{tip}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">ขั้นตอนทั่วไป ({t.stepsTh.length} step)</h2>
              <ol className="mt-4 space-y-3">
                {t.stepsTh.map((step, i) => (
                  <li key={i} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold">{i + 1}</div>
                      <div>
                        <div className="font-bold">{step.name}</div>
                        <p className="mt-1 text-sm text-muted-foreground">{step.text}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">เขต/ย่านยอดนิยมใน{c.nameTh}</h2>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {c.districtsTh.map((d, i) => (
                  <li key={i} className="rounded-lg border border-border bg-card p-3 text-sm font-semibold">{d}</li>
                ))}
              </ul>
              <div className="mt-4 rounded-xl border border-border bg-muted/30 p-4 text-sm">
                <div className="font-bold">ชุมชนเอ็กซ์แพท</div>
                <p className="mt-1 text-muted-foreground">{c.expatCommunitiesTh}</p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">คำถามที่พบบ่อย</h2>
              <div className="mt-4 space-y-3">
                {buildLocalFaqs(t, c).map((f, i) => (
                  <details key={i} className="rounded-xl border border-border bg-card p-4" open={i === 0}>
                    <summary className="cursor-pointer font-bold">{f.q}</summary>
                    <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border-2 border-amber-500/40 bg-amber-500/5 p-5">
              <h3 className="flex items-center gap-2 font-bold text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4" /> ข้อควรระวัง
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">{t.warnTh}</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-bold flex items-center gap-2"><Wifi className="h-4 w-4 text-primary" />อินเทอร์เน็ต & เดินทาง</h3>
              <p className="mt-2 text-xs text-muted-foreground"><b>เน็ต:</b> {c.internetTh}</p>
              <p className="mt-1 text-xs text-muted-foreground"><b>เดินทาง:</b> {c.transitTh}</p>
            </div>
            {t.servicePriceTHB > 0 && (
              <div className="rounded-2xl border border-primary bg-primary/5 p-5">
                <h3 className="font-bold">บริการของ NYC ใน{c.nameTh}</h3>
                <p className="mt-2 text-2xl font-extrabold text-primary">฿{t.servicePriceTHB.toLocaleString()}</p>
                <p className="mt-1 text-xs text-muted-foreground">รวมเอกสาร + ติดต่อหน่วยงาน + รับ-ส่ง</p>
                <a href="tel:+66922429293" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground">
                  <Phone className="h-4 w-4" /> โทรปรึกษา
                </a>
              </div>
            )}
          </aside>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl space-y-8">
          <div>
            <h2 className="text-xl font-extrabold">{t.titleTh} ในเมืองอื่น</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherCities.map((oc) => (
                <Link key={oc.slug} to="/expat-relocation/$topic/$city" params={{ topic: t.slug, city: oc.slug }} className="rounded-xl border border-border bg-card p-3 hover:border-primary">
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /><span className="font-bold text-sm">{t.titleTh} ใน{oc.nameTh}</span></div>
                  <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{oc.categories[t.category].costNoteTh}</div>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">ดู <ArrowRight className="h-3 w-3" /></span>
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-xl font-extrabold">หัวข้อ {EXPAT_CAT_LABELS_TH[t.category]} อื่นๆ ใน{c.nameTh}</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherTopicsSameCat.map((ot) => (
                <Link key={ot.slug} to="/expat-relocation/$topic/$city" params={{ topic: ot.slug, city: c.slug }} className="rounded-xl border border-border bg-card p-3 hover:border-primary">
                  <div className="flex items-center gap-2"><span className="text-xl">{ot.icon}</span><span className="font-bold text-sm">{ot.titleTh}</span></div>
                  <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{ot.taglineTh}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    </main>
  );
}
